import { VisuallyHidden } from "@mantine/core";
import { faDesktop, faMoonStars, faSunBright, type IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { Center } from "../center";
import { Icon } from "../icon";
import { Tooltip } from "../tooltip";

/** The colour schemes a user can choose. System stores Mantine's "auto", which follows the operating system. */
export enum ColorScheme {
    Light = "light",
    Dark = "dark",
    System = "auto",
}

/** The name users see for each colour scheme. */
export const COLOR_SCHEME_LABELS: Record<ColorScheme, string> = {
    [ColorScheme.Light]: "Light",
    [ColorScheme.Dark]: "Dark",
    [ColorScheme.System]: "System",
};

const THEME_OPTIONS: { value: ColorScheme; label: string; icon: IconDefinition }[] = [
    { value: ColorScheme.Light, label: COLOR_SCHEME_LABELS[ColorScheme.Light], icon: faSunBright },
    { value: ColorScheme.Dark, label: COLOR_SCHEME_LABELS[ColorScheme.Dark], icon: faMoonStars },
    { value: ColorScheme.System, label: COLOR_SCHEME_LABELS[ColorScheme.System], icon: faDesktop },
];

// Each option shows only its icon, so the name is in a tooltip for sighted users and hidden text for screen readers.
export const THEME_DATA = THEME_OPTIONS.map(({ value, label, icon }) => ({
    value,
    label: (
        <Tooltip label={label}>
            <Center w={32} h={26}>
                <Icon icon={icon} size="sm" aria-hidden />
                <VisuallyHidden>{label}</VisuallyHidden>
            </Center>
        </Tooltip>
    ),
}));

// The selected segment's background and icon colour for each option. System's background is the AI gradient, which
// a colour can't express, so the stylesheet draws it over this one.
export const THEME_SELECTED_COLORS: Record<ColorScheme, { background: string; icon: string }> = {
    [ColorScheme.Light]: {
        background: "var(--surface-primary-solid-color)",
        icon: "var(--mantine-color-text-title-1)",
    },
    [ColorScheme.Dark]: {
        background: "var(--surface-warning-solid-color)",
        icon: "var(--mantine-color-text-permanent-2)",
    },
    [ColorScheme.System]: {
        background: "var(--surface-primary-solid-color)",
        icon: "var(--mantine-color-text-permanent-1)",
    },
};

// While a cross-fade runs, the browser treats the pointer as being over the page itself. Anything the pointer is on (the
// user menu holding this picker, say) would see it leave, and a click would land outside it, so both close the menu.
// These are the events that tell an element the pointer left, and the presses that tell it a click landed outside.
export const CROSS_FADE_IGNORED_EVENTS = [
    "pointerout",
    "pointerleave",
    "mouseout",
    "mouseleave",
    "mousedown",
    "touchstart",
] as const;
